/**
 * Platform detection utilities for Capacitor native vs web.
 *
 * Import these helpers anywhere instead of checking window.Capacitor
 * directly, so platform logic stays in one place.
 */
import { Capacitor } from "@capacitor/core";

/** Optional browser preview flag to force native UI branches locally. */
const forceNativePreview = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("nativePreview") === "1";

/** True when running inside a Capacitor native app (iOS or Android). */
export const isNative = Capacitor.isNativePlatform() || forceNativePreview;

/** True when running in a standard web browser. */
export const isWeb = !isNative;

/** True when running inside a Capacitor iOS app. */
export const isNativeIOS = isNative && Capacitor.getPlatform() === "ios";

/**
 * Returns true when running on a real phone (native iOS app OR mobile web on a
 * small-screen touch device).  Returns false for desktop browsers even when the
 * viewport is narrow.
 *
 * Detection signals:
 *  - isNativeIOS  → Capacitor iOS native app (definitive)
 *  - (pointer: coarse) + small physical screen shortest side (≤768 px)
 *    → real mobile web browser on a phone
 *
 * Deliberately excludes viewport / window.innerWidth so resizing a desktop
 * browser never triggers this.
 */
export function isRealMobilePhone(): boolean {
  if (typeof window === "undefined") return false;
  const hasCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
  const shortestSide = Math.min(screen.width, screen.height);
  return hasCoarsePointer && shortestSide <= 768;
}

/**
 * Returns the correct auth redirect URL for the current platform.
 *
 * - Native: custom URL scheme that triggers a deep link back into the app
 * - Web: current page origin (Supabase default behavior)
 *
 * Use this wherever you call Supabase auth methods that accept
 * `emailRedirectTo` (signUp, resetPasswordForEmail, etc.).
 */
export function getAuthRedirectUrl(): string {
  if (isNative) {
    return "com.weightplanner.app://";
  }
  // Allow an explicit override via env var (useful for Vercel preview deployments
  // where the dynamic subdomain isn't in Supabase's allowed redirect URL list).
  const override = import.meta.env.VITE_AUTH_REDIRECT_URL as string | undefined;
  if (override) return override;
  return typeof window !== "undefined" ? window.location.origin : "";
}
